import {
  Component,
  inject,
  computed,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  signal,
  effect,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { EventManager } from '@app/shared/utils/event-manager';
import { SharedModule } from '@app/shared/shared.module';
import { ParagraphComponent } from '@app/shared/components/text/p/p.component';
import { DisplayComponent } from '@app/shared/components/display/display.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { BubbleLayoutService } from './animations/bubble-layout.service';
import { AnimationStep, BubbleConfig, UnlockCasinoData } from './types/unlock-casino.types';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UnlockCasinoAnimations } from './animations/unlock-casino.animations';
import { GsapService } from '../../shared/animations/gsap.service';
import { DEFAULT_BUBBLE_CONFIG } from '../casino/casino.constants';
import { FEATURES_WITH_MODAL } from '@app/shared/models/user.model';
import { PrizeUI, VALID_PRIZE_TYPES_SET } from '../progress-line.model';
import { ProgressLineFacade } from '../progress-line.facade';

@Component({
  selector: 'slotcom-unlock-casino',
  templateUrl: './unlock-casino.component.html',
  styleUrls: ['./unlock-casino.component.scss'],
  standalone: true,
  imports: [CommonModule, SharedModule, ParagraphComponent, DisplayComponent, ButtonComponent],
  providers: [BubbleLayoutService, UnlockCasinoAnimations],
})
export class UnlockCasinoComponent implements AfterViewInit, OnDestroy {
  data = signal<UnlockCasinoData | null>(null);
  readonly bubbleConfig: BubbleConfig = DEFAULT_BUBBLE_CONFIG;

  readonly bubblePadding = {
    left: this.bubbleConfig.tailSize + this.bubbleConfig.borderWidth / 2 + this.bubbleConfig.padding,
    right: this.bubbleConfig.borderWidth / 2 + this.bubbleConfig.padding,
    vertical: this.bubbleConfig.borderWidth / 2 + this.bubbleConfig.padding,
  };

  public readonly modalData = inject<Modal>(MAT_DIALOG_DATA);
  private readonly progressLineFacade = inject(ProgressLineFacade);

  readonly casinoTier = this.progressLineFacade.casinoTier;

  readonly julietAsset = computed(() => {
    const data = this.data();
    if (!data) return '';
    return `unlock-casino-juliet-${data.casino.order}`;
  });

  private readonly STEP_DURATIONS: Record<AnimationStep, number> = {
    [AnimationStep.Idle]: 0,
    [AnimationStep.ShowBackground]: 500,
    [AnimationStep.ShowJulietAndConfetti]: 1500,
    [AnimationStep.ShowBubble]: 2500,
    [AnimationStep.HideBubble]: 500,
    [AnimationStep.ShowCasinoCenter]: 1000,
    [AnimationStep.MoveCasinoUpAndExitJuliet]: 0,
    [AnimationStep.WaitForClick]: 0,
    [AnimationStep.HideAllContent]: 600,
    [AnimationStep.ShowSlotsTitle]: 800,
    [AnimationStep.ShowSlotsList]: 0,
    [AnimationStep.ShowFinalButton]: 0,
    [AnimationStep.End]: 0,
  };

  casinoName = computed(() => {
    const order = this.data()?.casino?.order ?? 1;
    return `home.progress-line.casino-${order}.name`;
  });

  readonly slotsList = computed(() => this.data()?.games ?? []);

  private readonly eventManager = inject(EventManager);
  private readonly gsapService = inject(GsapService);
  private readonly animations = inject(UnlockCasinoAnimations);
  private readonly bubbleLayout = inject(BubbleLayoutService);

  currentStep = signal<AnimationStep>(AnimationStep.Idle);
  isBubbleHidden = signal(true);
  layout = this.bubbleLayout.layout;

  @ViewChild('bgContainer') bgContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('confettiBack') confettiBack!: ElementRef<HTMLImageElement>;
  @ViewChild('confettiFront') confettiFront!: ElementRef<HTMLImageElement>;
  @ViewChild('juliet') juliet!: ElementRef<HTMLDivElement>;
  @ViewChild('textWrapper') textWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('bubbleContainer') bubbleContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('casinoLogo') casinoLogo!: ElementRef<HTMLDivElement>;
  @ViewChild('casinoInfo') casinoInfo!: ElementRef<HTMLDivElement>;
  @ViewChildren('rewardItem') rewardItems!: QueryList<ElementRef<HTMLImageElement>>;
  @ViewChild('confirmButton') confirmButton!: ElementRef<HTMLDivElement>;
  @ViewChild('slotsTitle') slotsTitle!: ElementRef<HTMLDivElement>;
  @ViewChild('slotsDisplay') slotsDisplay!: ElementRef<HTMLDivElement>;
  @ViewChildren('slotsIcons') slotsIcons!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('finalButton') finalButton!: ElementRef<HTMLDivElement>;

  private timerId?: ReturnType<typeof setTimeout>;

  readonly filteredPrizesUI = computed(() => {
    const prizes = this.data()?.prizesUI ?? [];
    return prizes.filter((prize) => this.shouldShowPrize(prize));
  });

  private shouldShowPrize(prize: PrizeUI): boolean {
    if (!VALID_PRIZE_TYPES_SET.has(prize.type)) {
      return false;
    }

    if (prize.type === 'feature') {
      return this.isFeatureWithModal(prize.icon);
    }

    return true;
  }

  private isFeatureWithModal(icon: string): boolean {
    const match = icon.match(/(\d+)$/);
    if (!match) return false;
    const featureId = parseInt(match[1], 10);
    return FEATURES_WITH_MODAL.includes(featureId);
  }

  constructor() {
    this.data.set(this.modalData.data.unlockCasinoData);

    effect(() => {
      const step = this.currentStep();
      this.handleStep(step);
    });
  }

  ngAfterViewInit(): void {
    this.closeCasinoMapIfOpen();
    this.bubbleLayout.setupBubble(this.bubbleContainer, this.bubbleConfig);
    this.resetStage();
    requestAnimationFrame(() => {
      this.startSequence();
    });
  }

  ngOnDestroy(): void {
    this.bubbleLayout.destroy();
    this.clearTimer();
    if (this.bubbleContainer) {
      this.gsapService.kill(this.bubbleContainer.nativeElement);
    }
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.UnlockCasinoComponent, ModalType.View));
  }

  handleClick(): void {
    const current = this.currentStep();
    if (current === AnimationStep.WaitForClick) {
      this.currentStep.set(AnimationStep.HideAllContent);
    } else if (current === AnimationStep.End) {
      this.closeModal();
    }
  }

  startSequence(): void {
    this.currentStep.set(AnimationStep.ShowBackground);
  }

  private resetStage(): void {
    // Background
    if (this.bgContainer?.nativeElement) {
      this.gsapService.set(this.bgContainer.nativeElement, { opacity: 0 });
    }

    // Juliet
    if (this.juliet?.nativeElement) {
      this.gsapService.set(this.juliet.nativeElement, { opacity: 0 });
    }

    // Confetti
    if (this.confettiBack?.nativeElement) {
      this.gsapService.set(this.confettiBack.nativeElement, { scale: 0, opacity: 0 });
    }
    if (this.confettiFront?.nativeElement) {
      this.gsapService.set(this.confettiFront.nativeElement, { scale: 0, opacity: 0 });
    }

    // Casino elements
    if (this.casinoLogo?.nativeElement) {
      this.gsapService.set(this.casinoLogo.nativeElement, { opacity: 0 });
    }
    if (this.casinoInfo?.nativeElement) {
      this.gsapService.set(this.casinoInfo.nativeElement, { opacity: 0 });
    }

    // Rewards
    const hiddenProps = { scale: 0, opacity: 0 };
    this.rewardItems?.forEach((item) => {
      this.gsapService.set(item.nativeElement, hiddenProps);
    });
    if (this.confirmButton?.nativeElement) {
      this.gsapService.set(this.confirmButton.nativeElement, hiddenProps);
    }

    // Slots
    if (this.slotsTitle?.nativeElement) {
      this.gsapService.set(this.slotsTitle.nativeElement, { opacity: 0 });
    }
    if (this.slotsDisplay?.nativeElement) {
      this.gsapService.set(this.slotsDisplay.nativeElement, { opacity: 0 });
    }
    if (this.finalButton?.nativeElement) {
      this.gsapService.set(this.finalButton.nativeElement, hiddenProps);
    }
    this.slotsIcons?.forEach((icon) => {
      this.gsapService.set(icon.nativeElement, hiddenProps);
    });
  }

  private handleStep(step: AnimationStep): void {
    this.clearTimer();
    const exitElements = [this.casinoLogo?.nativeElement, this.casinoInfo?.nativeElement].filter(
      (el): el is HTMLDivElement => !!el
    );

    switch (step) {
      case AnimationStep.Idle:
        this.scheduleNext(step);
        break;

      case AnimationStep.ShowBackground:
        if (this.bgContainer?.nativeElement) {
          this.gsapService.set(this.bgContainer.nativeElement, { opacity: 1 });
        }
        this.scheduleNext(step);
        break;

      case AnimationStep.ShowJulietAndConfetti:
        if (this.juliet?.nativeElement) {
          this.animations.julietEntrance(this.juliet.nativeElement);
        }
        if (this.confettiBack?.nativeElement && this.confettiFront?.nativeElement) {
          this.animations.confettiCelebration(this.confettiBack.nativeElement, this.confettiFront.nativeElement, false);
        }
        this.scheduleNext(step);
        break;

      case AnimationStep.ShowBubble:
        this.showBubble();
        this.scheduleNext(step);
        break;

      case AnimationStep.HideBubble:
        this.hideBubble();
        this.scheduleNext(step);
        break;

      case AnimationStep.ShowCasinoCenter:
        if (this.casinoLogo?.nativeElement) {
          this.animations.casinoLogoReveal(this.casinoLogo.nativeElement);
        }
        this.scheduleNext(step);
        break;

      case AnimationStep.MoveCasinoUpAndExitJuliet:
        if (this.casinoLogo?.nativeElement) {
          this.animations.casinoLogoToTop(this.casinoLogo.nativeElement, () => {
            if (this.casinoInfo?.nativeElement) {
              this.animations.casinoInfoReveal(this.casinoInfo.nativeElement, () => {
                this.playRewardsSequence();
              });
            }
          });
        }
        if (this.juliet?.nativeElement) {
          this.animations.julietExit(this.juliet.nativeElement);
        }
        break;

      case AnimationStep.WaitForClick:
        break;

      case AnimationStep.HideAllContent:
        this.animations.exitElements(exitElements);
        this.scheduleNext(step);
        break;

      case AnimationStep.ShowSlotsTitle:
        this.animations.slotsTitleReveal(this.slotsTitle?.nativeElement, this.slotsDisplay?.nativeElement);
        this.scheduleNext(step);
        break;

      case AnimationStep.ShowSlotsList:
        if (this.slotsIcons) {
          const slotElements = this.slotsIcons.map((el) => el.nativeElement);
          this.animations.slotsSequence(slotElements, () => {
            this.currentStep.set(AnimationStep.ShowFinalButton);
          });
        }
        break;

      case AnimationStep.ShowFinalButton:
        if (this.finalButton?.nativeElement) {
          this.gsapService.popIn(this.finalButton.nativeElement, {
            onComplete: () => this.currentStep.set(AnimationStep.End),
          });
        }
        break;

      case AnimationStep.End:
        break;
    }
  }

  private playRewardsSequence(): void {
    if (!this.rewardItems || !this.confirmButton?.nativeElement) {
      this.currentStep.set(AnimationStep.WaitForClick);
      return;
    }

    const rewards = this.rewardItems.map((el) => el.nativeElement);
    const button = this.confirmButton.nativeElement;

    this.animations.rewardsSequence(rewards, button, () => {
      this.currentStep.set(AnimationStep.WaitForClick);
    });
  }

  private scheduleNext(step: AnimationStep): void {
    const duration = this.STEP_DURATIONS[step];
    if (duration > 0) {
      this.timerId = setTimeout(() => {
        if (this.currentStep() < AnimationStep.End) {
          this.currentStep.set(this.currentStep() + 1);
        }
      }, duration);
    }
  }

  private clearTimer(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = undefined;
    }
  }

  private showBubble(): void {
    if (!this.bubbleContainer) return;
    this.isBubbleHidden.set(false);
    this.bubbleContainer.nativeElement.style.visibility = 'visible';
    this.gsapService.popIn(this.bubbleContainer.nativeElement);
  }

  private hideBubble(): void {
    if (!this.bubbleContainer) return;
    this.gsapService.shrinkOut(this.bubbleContainer.nativeElement, {
      onComplete: () => this.isBubbleHidden.set(true),
    });
  }

  private closeCasinoMapIfOpen(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.CasinoMapComponent, ModalType.View));
  }
}
